import * as Sentry from "@sentry/tanstackstart-react";
import { TaggedError } from "better-result";
import { z } from "zod/v4";
import {
  ApiError,
  type LiteralTitles,
  type MiddlewareErrorTitle,
  problemDetailsSchema,
  type TypedApiError,
  UnknownApiError,
} from "#/modules/util.auth/api-errors";
import { m } from "#/paraglide/messages";
import type { components, paths } from "#/schema";

type ProblemDetails = components["schemas"]["ProblemDetails"];

type ResponseTitles<T> = T extends { responses: infer R }
  ? {
      [S in keyof R]-?: R[S] extends { content: infer C }
        ? { [M in keyof C]-?: C[M] extends { title?: infer U } ? LiteralTitles<U> : never }[keyof C]
        : never;
    }[keyof R]
  : never;

type PathErrorTitle = {
  [P in keyof paths]-?: { [M in keyof paths[P]]-?: ResponseTitles<paths[P][M]> }[keyof paths[P]];
}[keyof paths];

type ErrorSchemaKey = Extract<keyof components["schemas"], `${string}Errors`>;

/**
 * 404 codes a user can recover from. The contract documents 404 globally (any endpoint may
 * NotFound, see NotFoundResponseOperationFilter), so these come from the exposed error enum
 * instead of a per-endpoint `*Errors` declaration; every other 404 keeps the not-found fallback.
 */
export type NotFoundErrorTitle = Extract<
  components["schemas"]["SportdeelnameError"],
  "SportNotFound"
>;

/**
 * Union of every error code any endpoint can emit, derived from the generated
 * `*Errors` schemas plus the inline per-response title enums (the two can diverge).
 * Regenerating the schema after a backend change makes the `satisfies` check
 * below fail until the new code gets a translation.
 */
export type KnownErrorTitle =
  | components["schemas"][ErrorSchemaKey]
  | PathErrorTitle
  | MiddlewareErrorTitle
  | NotFoundErrorTitle;

/** Per-call-site wording overrides for codes whose canonical message doesn't fit the context. */
export type ApiErrorOverrides = Partial<Record<KnownErrorTitle, (error: ProblemDetails) => string>>;

const generic = () => m["common.something_went_wrong"]();

/**
 * The single code → message registry. `satisfies` proves at compile time that every
 * error code the whole API can emit has been considered; codes users can't act on
 * deliberately map to the generic message.
 */
const apiErrorMessages = {
  // auth / account
  UserAlreadyExists: () => m["signup.api_errors.user_already_exists"](),
  InvalidCredentials: () => m["auth.api_errors.invalid_credentials"](),
  UserIsBlocked: () => m["auth.api_errors.user_is_blocked"](),
  EmailNotConfirmed: () => m["auth.api_errors.email_not_confirmed"](),
  EmailInvalid: () => m["auth.password_errors.invalid_email"](),
  EmailConfirmationFailed: () => m["auth.api_errors.email_confirmation_failed"](),
  EmailAlreadyConfirmed: () => m["auth.api_errors.email_already_confirmed"](),
  InvalidToken: () => m["auth.api_errors.invalid_token"](),
  PasswordResetFailed: () => m["auth.api_errors.password_reset_failed"](),
  // PasswordInvalid carries Identity validation codes as a JSON string[] in `detail`.
  PasswordInvalid: (error) => {
    if (!error.detail) return m["auth.api_errors.password_reset_failed"]();
    try {
      return translatePasswordValidationErrors(z.array(z.string()).parse(JSON.parse(error.detail)));
    } catch {
      return error.detail;
    }
  },
  RegistrationFailed: () => m["signup.api_errors.registration_failed"](),
  TwoFactorNotEnabled: () => m["auth.api_errors.two_factor_not_enabled"](),
  TwoFactorAlreadyEnabled: () => m["auth.api_errors.two_factor_already_enabled"](),
  TwoFactorCodeInvalid: () => m["auth.api_errors.two_factor_code_invalid"](),
  TwoFactorRecoveryCodeInvalid: () => m["auth.password_errors.recovery_code_redemption_failed"](),
  PasskeyInvalid: () => m["auth.api_errors.passkey_invalid"](),
  PasskeyVerificationFailed: () => m["auth.api_errors.passkey_verification_failed"](),
  TooManyAttempts: () => m["auth.api_errors.too_many_attempts"](),
  TenantAccessDenied: () => m["auth.api_errors.tenant_access_denied"](),
  TenantWriteRequiresMembership: () => m["auth.api_errors.tenant_write_requires_membership"](),

  // school
  SchoolAlreadyExists: () => m["brin.api_errors.duplicate_school"](),
  DocentAlreadyExists: () => m["invite.errors.docent_already_exists"](),
  UserIsNotHoofddocent: () => m["school.docenten.errors.user_is_not_hoofddocent"](),
  CannotRemoveHoofddocent: () => m["school.docenten.errors.cannot_remove_hoofddocent"](),
  MissingTenantHeader: () => m["beweegactiviteitenreeks.api_errors.missing_tenant_header"](),

  // invites
  InviteExpired: () => m["invite.errors.invite_expired"](),
  InviteInvalid: () => m["invite.errors.invite_invalid"](),
  InviteAlreadyAccepted: () => m["invite.errors.invite_already_accepted"](),
  InviteAlreadyExists: () => m["invite.errors.invite_already_exists"](),
  InviteeIsAdmin: () => m["invite.errors.invitee_is_admin"](),
  MaxDocentenReached: () => m["invite.errors.max_docenten_reached"](),
  NotAuthorized: () => m["invite.errors.not_authorized"](),

  // klassen / leerlingen / import
  KlasAlreadyExists: () => m["import.api_errors.class_already_exists"](),
  HasActiveInschrijvingen: () => m["import.api_errors.has_active_enrollments"](),
  LeerlingAlreadyExists: () => m["school.students.errors.leerling_already_exists"](),
  EmptyFile: () => m["import.api_errors.empty_file"](),
  InvalidFile: () => m["import.api_errors.invalid_file"](),
  InvalidHeaders: () => m["import.api_errors.invalid_headers"](),
  VerwerkersovereenkomstRequired: () => m["import.api_errors.verwerkersovereenkomst_required"](),

  // beweegactiviteiten / sociaal-emotioneel reeksen
  InvalidBeoordelingsschaal: () =>
    m["beweegactiviteitenreeks.api_errors.invalid_beoordelingsschaal"](),
  BeweegdoelRequired: () => m["beweegactiviteitenreeks.api_errors.beweegdoel_required"](),
  UpdateNotAllowed: () => m["beweegactiviteitenreeks.api_errors.update_not_allowed"](),
  MinimumThreeSociaalEmotioneelDoelenRequired: () =>
    m["sociaal_emotioneel_thema.api_errors.minimum_three_sociaalEmotioneelDoelen_required"](),
  MaximumFiveSociaalEmotioneelDoelenAllowed: () =>
    m["sociaal_emotioneel_thema.api_errors.maximum_five_sociaalEmotioneelDoelen_allowed"](),

  // tickets
  AssigneeNotAdmin: () => m["tickets.api_errors.assignee_not_admin"](),
  CannotDeleteFirstMessage: () => m["tickets.api_errors.cannot_delete_first_message"](),
  OnlyLatestMessageDeletable: () => m["tickets.api_errors.only_latest_message_deletable"](),
  NotAllowedWhileImpersonating: () => m["tickets.api_errors.not_allowed_while_impersonating"](),
  TooManyAttachments: () => m["tickets.too_many_files"](),

  // sportdeelname
  SportAlreadyExists: () => m["sportdeelname.api_errors.sport_already_exists"](),
  // 404: the sport left the catalogue while the picker was open — a refresh fixes it.
  SportNotFound: () => m["sportdeelname.api_errors.sport_not_found"](),

  // file uploads
  FileTooLarge: () => m["file_upload.api_errors.file_too_large"](),
  FileCannotBeEmpty: () => m["file_upload.api_errors.file_cannot_be_empty"](),
  InvalidFileType: () => m["file_upload.api_errors.invalid_file_type"](),
  UploadFailed: () => m["file_upload.api_errors.upload_failed"](),

  // lessen
  DocentAlreadyHasActiveLes: () => m["les.start.api_errors.docent_already_has_active_les"](),
  NoLeerlingenFoundForLes: () => m["les.start.api_errors.no_leerlingen_found_for_les"](),
  LesNotActief: () => m["les.start.api_errors.les_not_actief"](),
  LesNotInConcept: () => m["les.start.api_errors.les_not_in_concept"](),
} satisfies Record<KnownErrorTitle, (error: ProblemDetails) => string>;

/**
 * Overrides narrowed to the codes a specific endpoint can emit (inferred by `toResult`),
 * plus the middleware codes that can hit any endpoint. Impossible codes are compile errors.
 */
export type TypedApiErrorOverrides<T extends string> = Partial<
  Record<Extract<T | MiddlewareErrorTitle, KnownErrorTitle>, (error: ProblemDetails) => string>
>;

/**
 * Normalizes any API error shape to ProblemDetails: ApiError unwraps its captured body,
 * openapi-fetch error objects pass through, plain strings and UnknownApiError become `{ title }`.
 */
const titledProblemDetailsSchema = problemDetailsSchema.extend({ title: z.string().nullable() });
const validationProblemDetailsSchema = z.object({
  status: z.literal(422),
  errors: z.record(z.string(), z.array(z.string())),
});

export function toProblemDetails(cause: unknown): ProblemDetails {
  const plainTitle = z.string().safeParse(cause);
  if (plainTitle.success) return { title: plainTitle.data };
  if (cause instanceof UnknownApiError) return { title: cause.message };
  if (ApiError.is(cause)) {
    return { status: cause.status, ...cause.problem, title: cause.problem.title ?? cause.title };
  }
  const problem = titledProblemDetailsSchema.safeParse(cause);
  if (problem.success) return problem.data;
  return { title: "UnknownError" };
}

export function isKnownErrorTitle(title: string): title is KnownErrorTitle {
  return Object.hasOwn(apiErrorMessages, title);
}

/**
 * The one place where a failure turns into "Er ging iets onverwachts mis". The failed API call
 * itself is already a log line (fetch client), but not every generic message has an API call
 * behind it, and the value that reaches the form is never thrown, so nothing else records that
 * the user saw the generic message.
 */
function reportGenericMessage(problem: ProblemDetails) {
  Sentry.logger.warn(`generic error shown: ${problem.title ?? "UnknownError"}`, {
    "api.error.title": problem.title ?? "UnknownError",
    "api.error.status": problem.status ?? 0,
    "api.error.detail": problem.detail ?? "",
  });
}

/**
 * `{ formError }` for an internal dead end that has no API error behind it (a missing token, a
 * response without the expected body). `code` names the dead end in GlitchTip; the user sees the
 * generic message.
 */
export function genericFormError(code: string) {
  reportGenericMessage({ title: code });
  return { formError: generic() };
}

/**
 * Overrides for a given error type: narrowed to the endpoint's codes when the error is a
 * `TypedApiError` (from `toResult`), the full registry otherwise. A conditional type instead
 * of overloads — an overload would silently fall back to the untyped signature on a bad key.
 */
export type OverridesFor<E> =
  E extends TypedApiError<infer T> ? TypedApiErrorOverrides<T> : ApiErrorOverrides;

/**
 * Translates any API error to a user-facing message via the global registry.
 * Unknown titles fall back to the generic message, or the not-found message on a 404. When the
 * error is a `TypedApiError` (from `toResult`), the overrides are narrowed to that endpoint's codes.
 */
export function translateApiError<E>(error: E, overrides?: OverridesFor<E>): string {
  const problem = toProblemDetails(error);
  const title = problem.title;
  if (!title || !isKnownErrorTitle(title)) {
    if (problem.status === 404) return m["common.not_found_description"]();
    reportGenericMessage(problem);
    return generic();
  }
  // SAFETY: OverridesFor<E> only narrows the keys of ApiErrorOverrides; every value is a subset.
  const translate =
    (overrides as ApiErrorOverrides | undefined)?.[title] ?? apiErrorMessages[title];
  return translate(problem);
}

/**
 * Error form of {@link translateApiError} for loaders and auth bootstrap, where a failed API
 * call belongs in the error boundary.
 */
export function translatedApiError<E>(error: E, overrides?: OverridesFor<E>): Error {
  return new Error(translateApiError(error, overrides));
}

/**
 * An expected API failure returned from a mutation server fn (see `apiFailure`);
 * `toastErrorMiddleware` toasts it and throws this on the client.
 */
export class ApiFailureError extends TaggedError("ApiFailureError")<{
  key: string;
  message: string;
}> {}

export type ApiFailure = { apiError: { key: string; message: string } };

const apiFailureSchema = z.object({
  apiError: z.object({ key: z.string(), message: z.string() }),
});

export function isApiFailure<T>(value: T | ApiFailure): value is ApiFailure {
  return apiFailureSchema.safeParse(value).success;
}

/**
 * Maps a 422 validation error's field errors to translated strings; returns undefined
 * for any other error, so handlers can branch: field errors → return, else → throw.
 * `fieldMessages` replaces the backend message for specific field keys.
 */
export function toFieldErrors(
  cause: unknown,
  fieldMessages?: Record<string, () => string>,
): Record<string, string[]> | undefined {
  const validation = validationProblemDetailsSchema.safeParse(toProblemDetails(cause));
  if (!validation.success) return undefined;

  const mapped: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(validation.data.errors)) {
    const replacement = fieldMessages?.[field];
    mapped[field] = replacement
      ? [replacement()]
      : messages.map((message) => (message === "Required" ? m["validation.required"]() : message));
  }
  return mapped;
}

/** ASP.NET Identity validation codes (password/email policy) — not ProblemDetails titles. */
type PasswordValidationError =
  | "ConcurrencyFailure"
  | "DefaultError"
  | "DuplicateEmail"
  | "DuplicateRoleName"
  | "DuplicateUserName"
  | "InvalidEmail"
  | "InvalidRoleName"
  | "InvalidToken"
  | "InvalidUserName"
  | "LoginAlreadyAssociated"
  | "PasswordMismatch"
  | "PasswordRequiresDigit"
  | "PasswordRequiresLower"
  | "PasswordRequiresNonAlphanumeric"
  | "PasswordRequiresUniqueChars"
  | "PasswordRequiresUpper"
  | "PasswordTooShort"
  | "RecoveryCodeRedemptionFailed"
  | "UserAlreadyHasPassword"
  | "UserAlreadyInRole"
  | "UserLockoutNotEnabled";

const passwordValidationMessages = {
  ConcurrencyFailure: () => m["auth.password_errors.concurrency_failure"](),
  DefaultError: () => m["auth.password_errors.default_error"](),
  DuplicateEmail: () => m["auth.password_errors.duplicate_email"](),
  DuplicateRoleName: () => m["auth.password_errors.duplicate_role_name"](),
  DuplicateUserName: () => m["auth.password_errors.duplicate_user_name"](),
  InvalidEmail: () => m["auth.password_errors.invalid_email"](),
  InvalidRoleName: () => m["auth.password_errors.invalid_role_name"](),
  InvalidToken: () => m["auth.password_errors.invalid_token"](),
  InvalidUserName: () => m["auth.password_errors.invalid_user_name"](),
  LoginAlreadyAssociated: () => m["auth.password_errors.login_already_associated"](),
  PasswordMismatch: () => m["auth.password_errors.password_mismatch"](),
  PasswordRequiresDigit: () => m["auth.password_errors.password_requires_digit"](),
  PasswordRequiresLower: () => m["auth.password_errors.password_requires_lower"](),
  PasswordRequiresNonAlphanumeric: () =>
    m["auth.password_errors.password_requires_non_alphanumeric"](),
  PasswordRequiresUniqueChars: () => m["auth.password_errors.password_requires_unique_chars"](),
  PasswordRequiresUpper: () => m["auth.password_errors.password_requires_upper"](),
  PasswordTooShort: () => m["auth.password_errors.password_too_short"](),
  RecoveryCodeRedemptionFailed: () => m["auth.password_errors.recovery_code_redemption_failed"](),
  UserAlreadyHasPassword: () => m["auth.password_errors.user_already_has_password"](),
  UserAlreadyInRole: () => m["auth.password_errors.user_already_in_role"](),
  UserLockoutNotEnabled: () => m["auth.password_errors.user_lockout_not_enabled"](),
} satisfies Record<PasswordValidationError, () => string>;

function isPasswordValidationError(code: string): code is PasswordValidationError {
  return Object.hasOwn(passwordValidationMessages, code);
}

export function translatePasswordValidationError(error: string): string {
  return isPasswordValidationError(error) ? passwordValidationMessages[error]() : error;
}

export function translatePasswordValidationErrors(errors: string[]): string {
  return errors.map(translatePasswordValidationError).join(", ");
}
